import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ErrorSummary } from '@loctopus/ui';
import { api } from '../../lib/api';
import { toParsedError } from '../../lib/errorDisplay';
import { formatDateTime, formatMinor } from '../../lib/format';
import type { Order, OrderStatus } from '../../lib/types';
import {
  Badge,
  Button,
  Card,
  DataTable,
  FormField,
  Input,
  PageHeader,
  Select,
  Spinner,
} from '../../components/ui';
import { RequirePermission } from '../../lib/auth';

const STATUS_TONE: Record<OrderStatus, 'neutral' | 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'neutral',
  paid: 'success',
  fulfilled: 'success',
  partially_refunded: 'warning',
  refunded: 'danger',
  cancelled: 'danger',
  disputed: 'danger',
};

/**
 * docs/API.md lists `GET /v1/orders` (search/list, filterable) but no dedicated CSV export route,
 * even though `orders.export` is a real tenant permission (packages/rbac/src/permissions.ts).
 * Exporting client-side from the currently loaded page of results is a reasonable stand-in; flag
 * to the API agent whether export should become a real streaming endpoint for large result sets
 * instead (this only exports what's been fetched into the table, capped by `limit` below).
 */
function exportOrdersCsv(orders: Order[]) {
  const header = ['Order ID', 'Status', 'Buyer email', 'Total', 'Currency', 'Created'];
  const rows = orders.map((o) => [
    o.id,
    o.status,
    o.buyerEmail,
    (o.totalMinor / 100).toFixed(2),
    o.currency,
    new Date(o.createdAt).toISOString(),
  ]);
  const csv = [header, ...rows]
    .map((r) => r.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `orders-${Date.now()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export function OrdersPage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');

  const ordersQuery = useQuery({
    queryKey: ['orders', status, q],
    queryFn: () => api.orders.list({ status: status || undefined, q: q || undefined, limit: 100 }),
  });

  const orders = ordersQuery.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Orders"
        description="Search orders, view attendees, issue refunds."
        actions={
          <RequirePermission permission="orders.export">
            <Button
              variant="secondary"
              disabled={orders.length === 0}
              onClick={() => exportOrdersCsv(orders)}
            >
              Export CSV
            </Button>
          </RequirePermission>
        }
      />
      <Card className="mb-6">
        <div className="grid grid-cols-2 gap-4">
          <FormField label="Search" hint="Buyer email or order ID">
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="jane@example.com"
            />
          </FormField>
          <FormField label="Status">
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All</option>
              {[
                'pending',
                'paid',
                'fulfilled',
                'partially_refunded',
                'refunded',
                'cancelled',
                'disputed',
              ].map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </FormField>
        </div>
      </Card>
      {ordersQuery.isLoading ? (
        <Spinner />
      ) : ordersQuery.error ? (
        <ErrorSummary error={toParsedError(ordersQuery.error)} />
      ) : (
        <DataTable<Order>
          columns={[
            {
              key: 'id',
              header: 'Order',
              render: (o) => <span className="font-mono text-xs">{o.id}</span>,
            },
            { key: 'email', header: 'Buyer', render: (o) => o.buyerEmail },
            {
              key: 'status',
              header: 'Status',
              render: (o) => <Badge tone={STATUS_TONE[o.status]}>{o.status}</Badge>,
            },
            { key: 'total', header: 'Total', render: (o) => formatMinor(o.totalMinor, o.currency) },
            { key: 'created', header: 'Created', render: (o) => formatDateTime(o.createdAt) },
          ]}
          rows={orders}
          getRowKey={(o) => o.id}
          onRowClick={(o) => navigate(`/orders/${o.id}`)}
          emptyMessage="No orders match these filters."
        />
      )}
    </div>
  );
}
